import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const ProductList = ({ status, onEdit }) => {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (user?.id) {
      loadProducts();
    }
  }, [user, status]);

  const loadProducts = async () => {
    try {
      setLoading(true);
      setError('');

      const { data, error: fetchError } = await supabase?.from('provider_products')?.select('*')?.eq('provider_id', user?.id)?.eq('status', status)?.order('created_at', { ascending: false });

      if (fetchError) {
        if (fetchError?.code?.substring(0, 2) === '42') {
          console.error('Schema error:', fetchError?.message);
          throw fetchError;
        } else if (fetchError?.code === 'PGRST116') {
          setProducts([]);
          return;
        } else {
          console.log('Data error:', fetchError?.message);
          setError(fetchError?.message);
          return;
        }
      }

      setProducts(data || []);
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (productId, newStatus) => {
    try {
      const { error: updateError } = await supabase?.from('provider_products')?.update({ status: newStatus, updated_at: new Date()?.toISOString() })?.eq('id', productId)?.eq('provider_id', user?.id);

      if (updateError) {
        if (updateError?.code?.substring(0, 2) === '42') {
          console.error('Schema error:', updateError?.message);
          throw updateError;
        } else {
          console.log('Update error:', updateError?.message);
          return;
        }
      }

      loadProducts();
    } catch (err) {
      console.error('Error updating product:', err);
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Weet u zeker dat u dit product wilt verwijderen?')) {
      return;
    }

    try {
      const { error: deleteError } = await supabase?.from('provider_products')?.delete()?.eq('id', productId)?.eq('provider_id', user?.id);

      if (deleteError) {
        if (deleteError?.code?.substring(0, 2) === '42') {
          console.error('Schema error:', deleteError?.message);
          throw deleteError;
        } else {
          console.log('Delete error:', deleteError?.message);
          return;
        }
      }

      loadProducts();
    } catch (err) {
      console.error('Error deleting product:', err);
    }
  };

  const getCategoryBadge = (category) => {
    const styles = {
      zonnepanelen: 'bg-warning/10 text-warning border-warning/20',
      isolatie: 'bg-info/10 text-info border-info/20',
      warmtepomp: 'bg-success/10 text-success border-success/20',
      ventilatie: 'bg-primary/10 text-primary border-primary/20',
      thuisbatterij: 'bg-error/10 text-error border-error/20',
      waterontharder: 'bg-muted text-muted-foreground border-border',
      overig: 'bg-muted text-muted-foreground border-border',
    };
    return styles?.[category] || styles?.overig;
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        {[1, 2, 3]?.map((i) => (
          <div key={i} className="h-20 bg-muted rounded"></div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-error">Fout bij laden: {error}</p>
      </div>
    );
  }

  if (products?.length === 0) {
    return (
      <div className="text-center py-12">
        <Icon name="Package" size={48} className="mx-auto text-muted-foreground mb-4" />
        <p className="text-muted-foreground">Geen producten gevonden</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {products?.map((product) => (
        <div
          key={product?.id}
          className="border border-border rounded-lg p-4 hover:shadow-md transition-shadow"
        >
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-2">
                <h3 className="text-lg font-semibold text-foreground">
                  {product?.product_name}
                </h3>
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                    getCategoryBadge(product?.category)
                  }`}
                >
                  {product?.category}
                </span>
              </div>
              <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                {product?.description}
              </p>
              <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                <div className="flex items-center space-x-1">
                  <Icon name="Eye" size={14} />
                  <span>{product?.view_count || 0} weergaven</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Icon name="FileText" size={14} />
                  <span>{product?.quote_requests_count || 0} offerteaanvragen</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Icon name="DollarSign" size={14} />
                  <span>{product?.price_range || 'Prijs op aanvraag'}</span>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-2 ml-4">
              <Button
                size="sm"
                variant="outline"
                iconName="Edit"
                onClick={() => onEdit(product)}
              >
                Bewerken
              </Button>
              {status === 'draft' && (
                <Button
                  size="sm"
                  variant="success"
                  onClick={() => handleStatusUpdate(product?.id, 'active')}
                >
                  Publiceren
                </Button>
              )}
              {status === 'active' && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleStatusUpdate(product?.id, 'archived')}
                >
                  Archiveren
                </Button>
              )}
              {status === 'archived' && (
                <Button
                  size="sm"
                  variant="success"
                  onClick={() => handleStatusUpdate(product?.id, 'active')}
                >
                  Herstellen
                </Button>
              )}
              <Button
                size="sm"
                variant="danger"
                iconName="Trash2"
                onClick={() => handleDelete(product?.id)}
              />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default ProductList;